import React, { useEffect, useState, useCallback, useRef } from "react";
import { useOnboarding as useOnboardingContext } from "../../hooks/useOnboarding";
import { OnboardingStep, OnboardingData } from "../../types/onboarding";
import { useOnboardingFlow } from "../../hooks/useOnboardingFlow";
import SetupAccount from "../../pages/onboarding/SetupAccount";
import TwoColumnLayout from "../layout/TwoColumnLayout";
import ProgressBar from "../ProgressBar";
import StepWrapper from "./StepWrapper";
import styles from "../../pages/onboarding/OnboardingFlow.module.css";
import containerStyles from "./OnboardingContainer.module.css";

import SelectIntegrationsVC from "../../pages/onboarding/SelectIntegrationsVC";
import PrepareUpdate from "../../pages/onboarding/PrepareUpdate";
import SendUpdate from "../../pages/onboarding/SendUpdate";
import PaymentPlans from "../../pages/onboarding/PaymentPlans";
import OnboardingMetrics from "../../pages/onboarding/OnboardingMetrics";
import SelectContacts from "../../pages/onboarding/SelectContacts";
import OnboardingErrorBoundary from "./OnboardingErrorBoundary";
import { useAuth } from "../../contexts/AuthContext";

/**
 * Props injected into each step component.
 */
interface StepActionProps {
  onNext: (data?: any) => void;
  onBack?: () => void;
}

/**
 * Helper function to determine current step from onboarding data.
 * For VC invite onboarding, we skip role selection (step 2 in regular flow).
 */
const determineCurrentStepFromData = (data: OnboardingData): number => {
  if (!data) return 1;

  // If onboarding is complete, redirect will be handled elsewhere
  if (data.onboarding_complete) return 7;

  const completed = new Set(data.completed_steps || []);

  // Define step requirements in order for VC invite flow (no role selection)
  const stepRequirements: {
    step: number;
    requiredCompletion: string | null;
  }[] = [
    { step: 1, requiredCompletion: null }, // No prereqs - always accessible
    { step: 2, requiredCompletion: "account_setup" }, // Contacts (was step 3)
    { step: 3, requiredCompletion: "contacts_selection" }, // Metrics (was step 4)
    { step: 4, requiredCompletion: "metrics_selection" }, // Integrations (was step 5)
    { step: 5, requiredCompletion: "integrations_selection" }, // Prepare (was step 6)
    { step: 6, requiredCompletion: "prepare_update" }, // Send (was step 7)
    { step: 7, requiredCompletion: "send_update" }, // Payment (was step 8)
  ];

  // Find the highest step whose prerequisite is met
  let targetStep = 1; // Start at step 1 by default

  for (const { step, requiredCompletion } of stepRequirements) {
    if (!requiredCompletion || completed.has(requiredCompletion)) {
      // Prerequisites met for this step (or no prerequisites)
      targetStep = step;
    } else {
      // Prerequisites not met, can't go further
      break;
    }
  }

  return targetStep;
};

/**
 * Helper function to check if onboarding is complete from data
 */
const isOnboardingCompleteFromData = (data: OnboardingData): boolean => {
  return data?.onboarding_complete === true;
};

// Steps for VC invite onboarding (7 steps, skipping role selection)
const steps: OnboardingStep[] = [
  {
    id: 1,
    title: "Set Up Account",
    component: SetupAccount as React.ComponentType<StepActionProps>,
    imageSrc: "/assets/onboarding-img-1.png",
  },
  {
    id: 2,
    title: "Add Contacts",
    component: SelectContacts as React.ComponentType<StepActionProps>,
    imageSrc: "/assets/onboarding-img-3.png",
  },
  {
    id: 3,
    title: "Choose Metrics",
    component: OnboardingMetrics as React.ComponentType<StepActionProps>,
    imageSrc: "/assets/onboarding-img-5.png",
  },
  {
    id: 4,
    title: "Select Integrations",
    component: SelectIntegrationsVC as React.ComponentType<StepActionProps>,
    imageSrc: "/assets/onboarding-img-6.png",
  },
  {
    id: 5,
    title: "Prepare Update",
    component: PrepareUpdate as React.ComponentType<StepActionProps>,
    imageSrc: "/assets/onboarding-img-6.png",
  },
  {
    id: 6,
    title: "Send Update",
    component: SendUpdate as React.ComponentType<StepActionProps>,
    imageSrc: "/assets/onboarding-img-6.png",
  },
  {
    id: 7,
    title: "Free Trial",
    component: PaymentPlans as React.ComponentType<StepActionProps>,
    imageSrc: "/assets/onboarding-img-6.png",
  },
];

interface OnboardingContainerState {
  currentStep: number;
  isLoading: boolean;
  error: string | null;
  isInitialized: boolean;
  isTransitioning: boolean;
}

const VCInviteOnboardingContainer: React.FC = () => {
  const totalSteps = steps.length;
  const {
    data,
    loading,
    error: loadError,
    save,
    complete,
    completePortfolio,
    currentStep,
    isComplete,
  } = useOnboardingFlow();
  const { isOnboarded } = useAuth();
  const { setRole: setRoleInContext, isInitialized: contextInitialized } =
    useOnboardingContext();
  const [state, setState] = useState<OnboardingContainerState>({
    currentStep: 1,
    isLoading: true,
    error: null,
    isInitialized: false,
    isTransitioning: false,
  });

  // Check if running on localhost for development features
  const isLocalhost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1" ||
      window.location.hostname === "");

  // Debug mode toggle (persisted and URL-enabled)
  const [debugEnabled, setDebugEnabled] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem("onboarding_debug");
      if (stored != null) return stored === "true";
      const params = new URLSearchParams(window.location.search);
      return params.get("debug") === "1";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem("onboarding_debug", String(debugEnabled));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.debug("[Onboarding Debug] Failed to persist debug flag", e);
    }
  }, [debugEnabled]);

  // Console diff logs when data changes
  const prevDataRef = useRef<OnboardingData | null>(null);
  useEffect(() => {
    if (!debugEnabled) {
      prevDataRef.current = data;
      return;
    }
    const prev = prevDataRef.current;
    if (prev) {
      const keys: (keyof OnboardingData)[] = [
        "role",
        "metrics",
        "integrations",
        "step",
        "completed_steps",
      ];
      const diffs: Record<string, { before: any; after: any }> = {};
      for (const key of keys) {
        const before = (prev as any)[key];
        const after = (data as any)[key];
        const isEqual = JSON.stringify(before) === JSON.stringify(after);
        if (!isEqual) {
          diffs[key as string] = { before, after };
        }
      }
      if (Object.keys(diffs).length > 0) {
        // eslint-disable-next-line no-console
        console.log("[Onboarding Debug] Data changed:", diffs);
      }
    }
    prevDataRef.current = data;
  }, [data, debugEnabled]);

  // Initialize onboarding state using server data via useOnboardingFlow
  // Auto-set role to "Founder" for VC invite onboarding
  useEffect(() => {
    if (state.isInitialized) return;

    // Wait for both context initialization and server data loading
    if (!contextInitialized || loading) {
      setState((prev) => ({ ...prev, isLoading: true }));
      return;
    }

    // Auto-set role to Founder for VC invite onboarding
    if (!data.role || data.role !== "Founder") {
      setRoleInContext("Founder");
      void save({ role: "Founder" });
    }

    const step = determineCurrentStepFromData(data);
    setState((prev) => ({
      ...prev,
      currentStep: step,
      isInitialized: true,
      isLoading: false,
      error: loadError,
    }));

    // Defensive persistence: if computed step differs from stored step, persist it
    try {
      if (data.step !== step) {
        console.log(
          `[VC Invite Onboarding] Correcting step mismatch: stored=${data.step}, computed=${step}`
        );
        void save({ step });
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.debug("[VC Invite Onboarding] Failed to persist step on init", e);
    }
  }, [contextInitialized, loading, data, loadError, state.isInitialized, save, setRoleInContext]);

  // Redirect to dashboard when onboarding is complete
  useEffect(() => {
    if (!state.isInitialized) return;
    if (isComplete || isOnboarded === true) {
      // Clear VC invite data from localStorage
      localStorage.removeItem('portfolio_invite_data');
      localStorage.removeItem('vc_required_integrations');
      window.location.href = "/";
    }
  }, [isComplete, isOnboarded, state.isInitialized]);

  const handleNext = useCallback(
    async (stepData?: any) => {
      try {
        setState((prev) => ({ ...prev, error: null, isTransitioning: true }));

        // Step name mapping for VC invite flow (no role selection)
        const stepNames: { [key: number]: string } = {
          1: "account_setup",
          2: "contacts_selection",  // was step 3
          3: "metrics_selection",   // was step 4
          4: "integrations_selection", // was step 5
          5: "prepare_update",      // was step 6
          6: "send_update",         // was step 7
          7: "payment_complete",    // was step 8
        };

        const name = stepNames[state.currentStep];
        const completed = new Set([...(data.completed_steps || [])]);
        if (name) completed.add(name);

        const nextStep = Math.min(state.currentStep + 1, totalSteps);

        // Merge step-specific data
        const partial: Partial<OnboardingData> = {
          ...stepData,
          step: nextStep,
          completed_steps: Array.from(completed),
        };

        await save(partial);

        if (state.currentStep >= totalSteps) {
          // Use completePortfolio for VC-invited users to create the VC-managed workflow
          const result = await completePortfolio();
          if (!result.success) {
            setState((prev) => ({
              ...prev,
              error: result.error || "Failed to complete portfolio onboarding.",
              isTransitioning: false,
            }));
            return;
          }
          console.log("[VC Invite Onboarding] Portfolio onboarding complete, workflow created:", result.workflow);
          // redirect happens via effect
          return;
        }

        setState((prev) => ({
          ...prev,
          currentStep: nextStep,
          isTransitioning: false,
        }));
      } catch (e) {
        setState((prev) => ({
          ...prev,
          error: "Failed to save progress. Please try again.",
          isTransitioning: false,
        }));
      }
    },
    [state.currentStep, data.completed_steps, totalSteps, save, completePortfolio]
  );

  const handleBack = useCallback(() => {
    if (state.currentStep > 1) {
      const newStep = state.currentStep - 1;
      setState((prev) => ({
        ...prev,
        currentStep: newStep,
        isTransitioning: false,
      }));
      // Persist step when navigating back
      try {
        void save({ step: newStep });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.debug("[VC Invite Onboarding] Failed to persist step on back", e);
      }
    }
  }, [state.currentStep, save]);

  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  // Keep backend step in sync with local step changes
  useEffect(() => {
    if (!state.isInitialized) return;
    if (data.step !== state.currentStep) {
      try {
        void save({ step: state.currentStep });
      } catch (e) {
        // eslint-disable-next-line no-console
        console.debug("[VC Invite Onboarding] Failed to sync step", e);
      }
    }
  }, [state.currentStep, state.isInitialized, data.step, save]);

  // Show loading state while initializing
  if (state.isLoading || !state.isInitialized) {
    return (
      <main className={styles.main}>
        <div className={`${styles.card} ${containerStyles.container}`}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "3rem",
              flexDirection: "column",
              gap: "1rem",
            }}
          >
            <div className={containerStyles.spinner} />
            <p>Setting up your onboarding experience...</p>
          </div>
        </div>
      </main>
    );
  }

  const currentStepData = steps.find((s) => s.id === state.currentStep);
  if (!currentStepData) {
    return (
      <main className={styles.main}>
        <div className={`${styles.card} ${containerStyles.container}`}>
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <p>Step not found. Please refresh the page.</p>
          </div>
        </div>
      </main>
    );
  }

  const StepComponent =
    currentStepData.component as React.ComponentType<StepActionProps>;
  const progressPercentage = (state.currentStep / totalSteps) * 100;

  return (
    <OnboardingErrorBoundary>
      <div className={containerStyles.onboardingWrapper}>
        <main className={styles.main}>
          <div className={`${styles.card} ${containerStyles.container}`}>
            {state.error && (
              <div className={containerStyles.errorBanner}>
                <div className={containerStyles.errorContent}>
                  <strong>Error:</strong> {state.error}
                  <button
                    onClick={clearError}
                    className={containerStyles.errorClose}
                    aria-label="Dismiss error"
                  >
                    ×
                  </button>
                </div>
              </div>
            )}

            <TwoColumnLayout
              left={
                <>
                  <div className={containerStyles.stepHeader}>
                    <h1 className={styles.title}>{currentStepData.title}</h1>
                    <div className={styles.step}>
                      Step {state.currentStep} of {totalSteps}
                    </div>
                    <ProgressBar progress={progressPercentage} />
                  </div>
                  <StepWrapper>
                    <div
                      className={`${containerStyles.stepContent} ${
                        state.isTransitioning
                          ? containerStyles.transitioning
                          : ""
                      }`}
                    >
                      <StepComponent
                        onNext={handleNext}
                        onBack={state.currentStep > 1 ? handleBack : undefined}
                      />
                    </div>
                  </StepWrapper>
                </>
              }
              right={
                <img
                  src={currentStepData.imageSrc}
                  alt={`${currentStepData.title} illustration`}
                  className={containerStyles.illustration}
                />
              }
            />
          </div>
        </main>
      </div>
    </OnboardingErrorBoundary>
  );
};

export default VCInviteOnboardingContainer;

